import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';
import { MediaUploadModal } from './MediaUploadModal';

interface ImageUrlWithUploadProps {
  /** Current image URL value */
  value: string;
  /** Callback when URL changes (either via input or upload) */
  onChange: (url: string) => void;
  /** Site ID for media library access */
  siteId: string;
  /** Label for the field */
  label?: string;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Help text shown below the input */
  helpText?: string;
  /** Input name attribute (for form handling) */
  name?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Whether to show a preview of the image */
  showPreview?: boolean;
  /** Recommended dimensions to show in help text */
  recommendedSize?: string;
}

/**
 * A reusable component that combines a URL input with upload/media library functionality.
 * Used for OG images, social images, and other image URL fields.
 */
export const ImageUrlWithUpload: React.FC<ImageUrlWithUploadProps> = ({
  value,
  onChange,
  siteId,
  label = 'Image URL',
  placeholder = 'https://example.com/image.jpg',
  helpText,
  name,
  className = '',
  showPreview = true,
  recommendedSize,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleMediaUploaded = (mediaUrl: string, cloudfrontUrl?: string) => {
    // Prefer CloudFront URL if available for better performance
    const urlToUse = cloudfrontUrl || mediaUrl;
    onChange(urlToUse);
    setIsModalOpen(false);
  };

  const handleClearImage = () => {
    onChange('');
  };

  const finalHelpText =
    helpText || (recommendedSize ? `Recommended size: ${recommendedSize}` : undefined);

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}

      {/* Preview (if image exists and preview is enabled) */}
      {showPreview && value && (
        <div className="relative mb-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
          <img
            src={value}
            alt="Preview"
            className="w-full h-32 object-cover"
            onError={e => {
              // Hide broken image
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <button
            type="button"
            onClick={handleClearImage}
            className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
            title="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Input and buttons row */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="url"
            name={name}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium border border-gray-300"
          title="Upload or select from media library"
        >
          <Upload size={16} />
          <span className="hidden sm:inline">Upload</span>
        </button>
      </div>

      {/* Help text */}
      {finalHelpText && <p className="mt-1 text-xs text-gray-500">{finalHelpText}</p>}

      {/* Media Upload Modal */}
      <MediaUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        siteId={siteId}
        mediaType="image"
        assetType="image"
        currentMediaUrl={value}
        onMediaUploaded={handleMediaUploaded}
      />
    </div>
  );
};

export default ImageUrlWithUpload;
